import type { BghArticleDetail } from '~/common/types/bghArticleDetail';
import { useTranslation } from 'react-i18next';
import { useCatalogBghArticleDetail } from '~/features/catalog/hooks/useCatalogBghArticleDetail';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { PriceSteps } from '~/features/catalog/components/articleDetail/PriceSteps';
import { PrinterIcon } from 'lucide-react';
import { formatDateByLanguage } from '~/common/utils/dateUtil';
import { RecentOrder } from '~/features/catalog/components/articleDetail/RecentOrder';
import { ArticleIcons } from '~/features/catalog/components/article/ArticleIcons';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { AddToCart } from '~/features/catalog/components/AddToCart';
import { KeyFeatureList } from '~/features/catalog/components/articleDetail/KeyFeatureList';
import { NutritionList } from '~/features/catalog/components/articleDetail/NutritionList';
import { ProductInformationDownloadsAndLinks } from '~/features/catalog/components/articleDetail/ProductInformationDownloadsAndLinks';
import { SupplierInfo } from '~/common/components/profitPlus/SupplierInfo';
import type { AdditionalInformationItem } from '~/features/catalog/components/articleDetail/AdditionalInformationList';
import AdditionalInformationList from '~/features/catalog/components/articleDetail/AdditionalInformationList';
import { useMemo } from 'react';
import { DownloadButton } from '~/common/components/DownloadButton';
import { useBasePath } from '~/common/hooks/useBasePath';
import { CatalogArticleNotAvailable } from '~/features/catalog/components/CatalogArticleNotAvailable';
import { cn } from '~/common/utils/styleUtil';
import type { ArticleInfoItem } from '~/common/components/ArticleInfoList';
import { ArticleInfoList } from '~/common/components/ArticleInfoList';
import ArticleComparisonPrice from '~/features/catalog/components/article/ArticleComparisonPrice';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/common/components/ui/tooltip.tsx';
import { PriceBadgeGroup } from '~/common/components/PriceBadgeGroup';
import { useNavigate, useSearchParams } from 'react-router';
import type { ArticleChangeInfo } from '~/common/types/articleChangeInfo.ts';
import { useSpecialDeliveryDate } from '~/shell/context/specialDeliveryDateContext';

export default function BghArticleDetailView({
  article,
  isAuthenticated,
  className,
  initialQuantity
}: Readonly<{
  article: BghArticleDetail;
  isAuthenticated: boolean;
  className?: string;
  initialQuantity?: number;
}>) {
  const { t, i18n } = useTranslation();
  const catalogArticle = useCatalogBghArticleDetail(article, initialQuantity);
  const specialDeliveryDate = useSpecialDeliveryDate(catalogArticle.mainArticleId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, path } = useBasePath();

  const handleNavigateToArticle = (changeInfo: ArticleChangeInfo) => {
    const searchTerm = searchParams.get('searchTerm');
    const articleDetailPath = `${path}/catalog/article/${changeInfo.articleNumber}`;
    const targetPath = searchTerm ? `${articleDetailPath}?searchTerm=${searchTerm}` : articleDetailPath;

    navigate(targetPath, {
      replace: true
    });
  };

  const items = useMemo(() => {
    const additionalInformationItems: AdditionalInformationItem[] = [
      {
        label: t('Wichtige Merkmale'),
        component: (
          <KeyFeatureList
            article={catalogArticle}
            specialDeliveryDate={specialDeliveryDate}
            orderEndTimesText={catalogArticle.orderEndTimesValue}
          />
        )
      }
    ];
    if (catalogArticle.showFactSheet && catalogArticle.nutritionFact) {
      additionalInformationItems.push({
        label: t('Nährwerte'),
        component: <NutritionList nutrition={catalogArticle.nutritionFact} />
      });
    }

    if (isAuthenticated) {
      additionalInformationItems.push({
        label: t('Dokumente und Links'),
        component: <ProductInformationDownloadsAndLinks article={article} />
      });
    }

    if (catalogArticle.isProfitPlus && catalogArticle.supplierGroupName && catalogArticle.supplierGroupMinOrderValue) {
      additionalInformationItems.push({
        label: t('Preis- und Lieferkonditionen'),
        component: <SupplierInfo article={article} />
      });
    }

    return additionalInformationItems;
  }, [article, catalogArticle, isAuthenticated, t, specialDeliveryDate]);

  const rohstoffHerkunftText = useMemo(() => {
    if (catalogArticle.rohstoffHerkunft && catalogArticle.rohstoffHerkunft.length > 0) {
      const maxItems = 3; // Nach Anforderung nur 3 anzeigen
      const selectedItems = catalogArticle.rohstoffHerkunft.slice(0, maxItems).map(x => x.text);
      const suffix = catalogArticle.rohstoffHerkunft.length > maxItems ? ' ...' : '';

      return selectedItems.join(', ') + suffix;
    }
    return '';
  }, [catalogArticle.rohstoffHerkunft]);

  const factSheetUrl = `/${lang}/resources/articles/${article.articleNumber}/factsheet`;

  const articleInfoItems: ArticleInfoItem[] = useMemo(
    () => [
      { label: t('Art. Nr.'), value: catalogArticle.articleNumber },
      { label: t('Marke'), value: catalogArticle.brand },
      { label: t('Rohstoffherkunft'), value: rohstoffHerkunftText }
    ],
    [catalogArticle.articleNumber, catalogArticle.brand, rohstoffHerkunftText, t]
  );

  return (
    <div className={cn(className)} key={catalogArticle.articleNumber}>
      <div className={'flex flex-col md:flex-row flex-wrap md:flex-nowrap md:gap-8 mx-4 md:mx-0'}>
        <div className={'flex justify-center items-center w-full'}>
          <TgsArticleImage
            celumId={catalogArticle.celumId}
            articleNumber={catalogArticle.articleNumber}
            alt={catalogArticle.description}
            size={'large'}
          />
        </div>
        <div className={'mt-2 md:mt-0 pr-0 md:pr-4 lg:pr-0 w-full'}>
          <div className={'flex justify-between items-start'}>
            <ArticlePrice
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              size={'lg'}
              isHit={catalogArticle.isHit}
            />
            {isAuthenticated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DownloadButton
                    name={`${catalogArticle.articleNumber}.pdf`}
                    url={factSheetUrl}
                    icon={<PrinterIcon className={'text-secondary'} />}
                    data-testid={'download-button'}
                  />
                </TooltipTrigger>
                <TooltipContent side={'top'}>{t('Kurzspezifikation drucken')}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <ArticleComparisonPrice
            className={'mt-2'}
            comparisonPrice={catalogArticle.comparisonPrice}
            hasComparisonPrice={catalogArticle.hasComparisonPrice}
            comparisonPriceUnitText={catalogArticle.comparisonPriceUnitText}
          />
          {catalogArticle.graduatedPriceSteps && (
            <div className={'my-2'}>
              <PriceSteps article={article} />
            </div>
          )}
          <PriceBadgeGroup
            direction="wrap"
            badges={[
              ...(article.graduatedPriceSteps?.length ? [{ type: 'gross' as const }] : []),
              ...(article.noveltyUntil ? [{ type: 'new' as const }] : []),
              ...(isAuthenticated && article.isHit ? [{ type: 'hit' as const }] : [])
            ]}
          />
          <div className={'my-5'}>
            <div>
              <h1
                className={cn(
                  'text-2xl xl:text-4xl py-3',
                  catalogArticle.isAction && !catalogArticle.isHit ? 'text-primary' : 'primary'
                )}
                data-testid={'article-description'}
              >
                {catalogArticle.description}
              </h1>
              <ArticleInfoList items={articleInfoItems} />
              <div>
                {specialDeliveryDate && (
                  <div data-testid={'next-delivery-date'}>
                    {t('Frühester Liefertermin')}: {formatDateByLanguage(specialDeliveryDate, i18n.language)}
                  </div>
                )}
                {catalogArticle.orderEndTimesText && (
                  <div data-testid={'order-end-times'}>{catalogArticle.orderEndTimesText}</div>
                )}
                {catalogArticle.isProfitPlus && <div>{t('Es gelten spezielle Preis- und Lieferkonditionen!')}</div>}
                {(catalogArticle.isProfitPlus || specialDeliveryDate) && <div>{t('Kein Rückgaberecht')}</div>}
              </div>
            </div>
          </div>
          {catalogArticle.recentOrderDate && (
            <div className={'desktop:my-10'}>
              <RecentOrder article={article} />
            </div>
          )}
          {catalogArticle.icons && catalogArticle.icons.length > 0 && (
            <div className={'my-4 flex items-center'}>
              <ArticleIcons icons={catalogArticle.icons} />
            </div>
          )}
          <ArticleSellUnit
            className={'desktop:mt-10'}
            selectedArticle={catalogArticle}
            allArticles={catalogArticle.allArticles}
            onArticleChange={
              changeInfo => setTimeout(() => handleNavigateToArticle(changeInfo), 300) // 300ms delay to avoid flickering
            }
            size={'lg'}
          />
          <div className={'group'}>
            <AddToCart
              articleNumber={catalogArticle.articleNumber}
              articleDescriptionText={catalogArticle.description}
              mainArticleId={catalogArticle.mainArticleId}
              isExternalArticle={false}
              isProfitPlus={catalogArticle.isProfitPlus}
              calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
              quantity={catalogArticle.quantity}
              onQuantityChange={catalogArticle.setQuantity}
              isAuthenticated={isAuthenticated}
              className={'min-w-[200px]'}
              showLargeButtonOnDesktop={true}
              hideCart={catalogArticle.hideCart}
              hideAddToTemplate={catalogArticle.isInactive}
            />
            {(catalogArticle.hideCart || catalogArticle.showSellOffMessage) && (
              <CatalogArticleNotAvailable
                showSellOffMessage={catalogArticle.showSellOffMessage}
                showCurrentlyNotAvailableMessage={catalogArticle.showCurrentlyNotAvailableMessage}
                substituteArticleNumber={catalogArticle.substituteArticleNumber}
                substituteArticleText={catalogArticle.substituteArticleText}
                notInWebshop={catalogArticle.notInWebshop}
                className={'mt-2'}
              />
            )}
          </div>
        </div>
      </div>
      <div className={'desktop: mt-8'}>
        <AdditionalInformationList items={items} />
      </div>
    </div>
  );
}
